import { createSelector } from '@reduxjs/toolkit'
import MomentFilter from '../../../helpers/MomentFilter'
import { jsonApiWrapper } from '../../../jsonApiParser'
import {
  getBrandId,
  getContent,
  getCurrentStoryId,
  getFilters,
  getWeekViewStartDay,
  getWeekViewEntityData,
  getWeekViewWidth,
} from '../../getters'
import { selectCurrentBrandId } from '../entities/brands'
import { selectV1LabelSets } from '../entities/labels'
import { selectMoments } from '../entities/moments'
import { selectCurrentStoryId } from '../entities/stories'
import { parseMomentData } from './helpers'

/**
 * Takes an array of moments and runs them
 * through the moment filter helper. Returns
 * moments that should be visible based on
 * selected filters.
 */
export const filterMoments = (momentList, filters) =>
  MomentFilter.inBulk({
    moments: momentList,
    selectedFilters: filters.selectedFilters,
    filterValues: filters.filterValues,
    disabledValues: filters.disabledValues,
  })

/**
 * Content-first filter. Unlike `filterMoments`, each content item is
 * matched against its own attributes with no fallback to the parent
 * moment. Used by Week View's Content mode so filters pick content
 * individually (e.g. only the Facebook post under a multi-channel
 * moment, not every post on that moment).
 */
export const filterContent = (contentList, filters) =>
  MomentFilter.contentInBulk({
    content: contentList,
    selectedFilters: filters.selectedFilters,
    filterValues: filters.filterValues,
    disabledValues: filters.disabledValues,
  })

/**
 * Helper method that takes a JSON API entity
 * and returns one that with proxied links
 * to its relationships.
 */
const getProxiedEntity = (entity, state) =>
  jsonApiWrapper({
    data: entity,
    state,
  })

/**
 * Selector for the number of active filters.
 */
export const selectActiveFiltersCount = createSelector(
  [getFilters],
  (filters) => {
    if (filters.filtersDisabled) return 0
    return filters && filters.value.length - filters.disabledValues.length
  }
)

/**
 * Selector for active filters.
 */
export const selectActiveFilters = createSelector([getFilters], (filters) => {
  if (!filters || filters.filtersDisabled) return []
  return filters.value.filter(
    (filter) => !filters.disabledValues.includes(filter)
  )
})

/**
 * Selector for selected filters.
 */
export const selectSelectedFilters = createSelector([getFilters], (filters) => {
  if (!filters) return undefined
  return filters.selectedFilters
})

export const selectWeekViewWidth = createSelector(
  [getWeekViewWidth],
  (width) => width
)

const calculatePreConstraint = (currentBrandId, storyId) =>
  storyId
    ? { field: 'storyInView.id', value: storyId.toString() }
    : { field: 'brand.id', value: currentBrandId && currentBrandId.toString() }

const selectWeekViewEntityData = createSelector(
  [getWeekViewEntityData],
  (weekViewEntityData) => weekViewEntityData
)

/**
 * Selector for Week View Moment indices.
 */
export const selectMomentIndices = createSelector(
  [selectWeekViewEntityData],
  (weekViewEntityData) => {
    if (!weekViewEntityData) return null
    return weekViewEntityData.moments
  }
)

/**
 * Selector for all Moments visible in Week View.
 */
const selectMomentsByIndices = createSelector(
  [selectMoments, selectMomentIndices],
  (moments, weekViewMomentIndices) => {
    if (!moments || !weekViewMomentIndices) return null
    const visibleMoments = {}
    let hasMomentsToShow = false

    Object.keys(weekViewMomentIndices).forEach((id) => {
      if (!moments[id] || moments[id].is_visible === false) return
      visibleMoments[id] = moments[id]
      hasMomentsToShow = true
    })

    return hasMomentsToShow ? visibleMoments : null
  }
)

/**
 * Selector for the ids of all content on the moments in Week View, before
 * filters. A moment lists its content wherever that content is dated, so this
 * is the content its card needs; the week's date query misses whatever is
 * dated outside the week.
 */
export const selectMomentContentIds = createSelector(
  [selectMomentsByIndices],
  (moments) => {
    if (!moments) return []
    const ids = new Set()
    Object.values(moments).forEach((moment) => {
      moment.relationships?.posts?.forEach(({ id }) => ids.add(id))
    })
    return [...ids].sort()
  }
)

/**
 * Selector for moments wrapped in the proxied getters.
 */
export const selectProxiedMoments = createSelector(
  [selectMomentsByIndices],
  (moments) => {
    if (!moments) return null
    const proxiedMoments = {}
    Object.keys(moments).forEach((id) => {
      if (!moments[id] || moments[id].is_visible === false) return
      proxiedMoments[id] = getProxiedEntity(
        moments[id],
        window.Higgs.store.getState()
      )
    })
    return proxiedMoments
  }
)

export const selectContentCount = createSelector(
  [selectWeekViewEntityData],
  (weekViewEntityData) => {
    if (!weekViewEntityData || !weekViewEntityData.meta) return 0
    return weekViewEntityData.meta.totalContentInWeek
  }
)

/**
 * Selector for Week View Content indices.
 */
const selectContentIndices = createSelector(
  [selectWeekViewEntityData],
  (weekViewEntityData) => {
    if (!weekViewEntityData) return null
    return weekViewEntityData.content
  }
)

/**
 * Selector for all Content.
 */
export const selectContent = createSelector(
  [getContent, selectContentIndices],
  (content, weekViewContentIndices) => {
    if (!content || !weekViewContentIndices) return null
    const wrappedContent = {}
    Object.keys(weekViewContentIndices).forEach((id) => {
      if (!content[id]) return
      wrappedContent[id] = getProxiedEntity(
        content[id],
        window.Higgs.store.getState()
      )
    })
    return wrappedContent
  }
)

/**
 * Selector for moments modified with necessary changes
 * for Week View.
 */
export const selectModifiedMoments = createSelector(
  [
    selectProxiedMoments,
    selectMomentIndices,
    selectContent,
    selectCurrentBrandId,
    selectCurrentStoryId,
    selectWeekViewWidth,
  ],
  (moments, weekViewMomentIndices, content, brandId, storyId, width) => {
    if (!moments || !weekViewMomentIndices) return null
    const modifiedMoments = {}
    let hasMomentsToShow = false

    Object.keys(weekViewMomentIndices).forEach((id) => {
      if (!moments[id] || moments[id].is_visible === false) return
      modifiedMoments[id] = parseMomentData(
        moments[id],
        content,
        storyId,
        brandId,
        width
      )
      hasMomentsToShow = true
    })

    return hasMomentsToShow ? modifiedMoments : null
  }
)

/**
 * Selector for all visible Moments with filters applied.
 */
export const selectFilteredMoments = createSelector(
  [selectModifiedMoments, getFilters],
  (moments, filters) => {
    if (!filters || !filters.hasActiveFilters) return moments
    return filterMoments(moments, filters)
  }
)

/**
 * Selector for Content with filters applied (content-first matching).
 * Used by Week View's Content mode. When no filters are active, returns
 * the full content map unchanged so the caller always has a single
 * source to iterate.
 */
export const selectFilteredContent = createSelector(
  [selectContent, getFilters],
  (content, filters) => {
    if (!content) return null
    if (!filters || !filters.hasActiveFilters) return content
    return filterContent(content, filters)
  }
)

/**
 * Aggregate a list of all Channels that are present on Posts
 * for the currrent week.
 */
export const selectCurrentWeekChannels = createSelector(
  [selectFilteredMoments],
  (moments) => {
    if (!moments || Object.keys(moments).length === 0) return null

    const channels = Object.values(moments).reduce(
      (channelCollection, moment) => {
        const channels = moment.services

        if (!channels || (Array.isArray(channels) && channels.length === 0)) {
          return channelCollection
        }

        channels.forEach((channel) => {
          const channelAlreadyCaptured = Boolean(
            channelCollection.find((c) => c.id === channel.id)
          )
          if (!channelAlreadyCaptured) channelCollection.push(channel)
        })

        return channelCollection
      },
      []
    )

    return channels.length === 0 ? null : channels
  }
)

/**
 * Selector for current week label sets.
 * Generates a collection of all label sets, and among those label sets generates
 * an array of labels in-use by the Moments in the given week. A label set
 * without any usage in the given week will have an empty labels array.
 */
export const selectCurrentWeekLabelSets = createSelector(
  [selectV1LabelSets, selectFilteredMoments],
  (labelSets, filteredMoments) => {
    if (!labelSets || !filteredMoments || filteredMoments.length === 0)
      return null
    const distinctLabelSets = Object.values(labelSets).reduce((acc, ls) => {
      acc[ls.id] = {
        id: ls.id,
        name: ls.name,
        label_sort_method: ls.label_sort_method,
        labels: [],
      }
      return acc
    }, {})

    const momentsLabelSets = Object.values(filteredMoments).reduce(
      (acc, moment) => {
        moment.labelSets?.forEach((momentLabelSet) => {
          const momentLabelSetId = momentLabelSet.id
          const labelSet = acc[momentLabelSetId]

          moment.labels
            ?.filter((label) => label.label_set.id === momentLabelSetId)
            ?.forEach((label) => {
              if (labelSet?.labels?.every(({ id }) => id !== label.id)) {
                labelSet.labels.push({
                  id: label.id,
                  name: label.name,
                  position: label.position,
                })
              }
            })

          acc[momentLabelSetId] = labelSet
        })

        return acc
      },
      distinctLabelSets
    )
    return momentsLabelSets
  }
)

export const selectPreConstraint = createSelector(
  [getBrandId, getCurrentStoryId],
  (brandId, storyId) => calculatePreConstraint(brandId, storyId)
)

export const selectWeekViewStartDay = createSelector(
  [getWeekViewStartDay],
  (startDay) => startDay
)
